import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './concretedOperationsTable.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import { getPerformanceConcretedOperationsTable } from '@/store/slices/components/dashboard/service';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import ConcretedOperationsTableRow from './ConcretedOperationsTableRow';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function ConcretedOperationsTable() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { concretedOperations } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const headers = [
    {
      id: 1,
      title: Trans(translations, 'Property'),
      subtitle: Trans(translations, 'Location'),
      field: TABLE_SORT_FIELDS.PROPERTY_ADDRESS,
    },
    {
      id: 2,
      title: Trans(translations, 'Operation'),
      subtitle: Trans(translations, 'PropertyType'),
      field: TABLE_SORT_FIELDS.OPERATION,
    },
    {
      id: 3,
      title: Trans(translations, 'Value'),
      field: TABLE_SORT_FIELDS.OPERATION_AMOUNT,
    },

    {
      id: 4,
      title: Trans(translations, 'Contact'),
      subtitle: Trans(translations, 'Origin'),
      field: TABLE_SORT_FIELDS.CONTACT_NAME,
    },
    {
      id: 5,
      title: Trans(translations, 'Agent'),
      subtitle: Trans(translations, 'Branch'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 6,
      title: Trans(translations, 'DashboardTableColumn.IncomeDate'),
      subtitle: Trans(translations, 'DashboardTableColumn.ClosingDate'),
      // field: TABLE_SORT_FIELDS.LISTING_DATE,
    },
  ];

  useEffect(() => {
    if (!concretedOperations.has_next) return;
    if (!isInViewport || concretedOperations.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(concretedOperations, {
      withNextPage: true,
    });

    dispatch(getPerformanceConcretedOperationsTable({ query, tableQuery }));
  }, [isInViewport]);

  const handleCellClick = (header) => {
    dispatch(
      setPerformanceModals({
        key: 'concretedOperations',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.concretedOperations(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      id="concreted-operations-table"
      className={`${parentStyles.table} ${specificStyles.concretedOperationsTable}`}
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={concretedOperations.sortBy}
            sortDirection={concretedOperations.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {concretedOperations.list.map((row) => (
          <ConcretedOperationsTableRow key={row.id} row={row} />
        ))}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(concretedOperations.loading || concretedOperations.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
